<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, shallowRef, markRaw } from 'vue'
import { useLayersStore } from '@/stores/layers'

import 'mapbox-gl/dist/mapbox-gl.css'
import mapboxgl from 'mapbox-gl'

import { MAPBOX_ACCESS_TOKEN } from '@/config/mapbox'
import { makeTransformRequest } from '@/lib/mapSigning'
import { useZLayers, releaseZLayers } from '@/composables/useZLayers'
import { blankStyle, getBasemap, applyMapViewToggles } from '@/components/maps/basemaps'
import { registerInstance, unregisterInstance } from '@/utils/mapbox/instanceRegistry'
import { createMapImageLoader } from '@/utils/mapbox/mapImageCache'
import { takePendingCamera, scheduleWriteUrl } from '@/utils/mapUrlState'

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN

// The single map component. Given a `map` object it owns exactly one Mapbox
// instance — creates it, applies the map's basemap, keeps it sized, and exposes
// the live instance via the default slot (and an @ready event). It knows nothing
// about "global" vs "tile": callers compose layers/draw/overlays as children and
// decide what to do with the instance (e.g. commit it to geo/SET_MAP).

const props = defineProps<{
  map: any                       // the map object { id, name, basemap, ... }
  initialOptions?: { center: [number, number]; zoom: number } | null
  // Registry / z-layer key. Defaults to map.id; the global canvas passes a
  // constant so it never collides with a tile showing the same map.
  instanceKey?: string
}>()

const emit = defineEmits(['ready'])

const layersStore = useLayersStore()
const key = props.instanceKey ?? props.map.id

const containerRef = ref<HTMLElement | null>(null)
const instance = shallowRef<any>(null)
const ready = ref(false)

const transformRequest = makeTransformRequest()
const imageLoader = createMapImageLoader()
const zLayers = useZLayers(instance as any, key)

const getLocalMapOptions = () => {
  try {
    const stored = localStorage.getItem('mapOptions')
    return stored ? JSON.parse(stored) : null
  } catch (e) {
    return null
  }
}
const defaultOptions = { center: [-91.874, 42.76], zoom: 4 }

// Resizing a Mapbox canvas re-renders every tile, so doing it per animation
// frame while the sidebar rail or a split divider is dragged reads as constant
// blinking. Instead: stretch + blur the existing frame for the duration of the
// gesture and do exactly one real resize once the box has been still for
// RESIZE_SETTLE_MS.
const RESIZE_SETTLE_MS = 160

let resizeObserver: ResizeObserver | null = null
let resizeTimer: ReturnType<typeof setTimeout> | null = null
const resizing = ref(false)

// Resolve the initial style from the map's basemap so we don't create with
// `outdoors` and then immediately setStyle again on load (that redundant reload
// raced the draw layer-raise → "Style is not done loading"). Non-Mapbox basemaps
// (rasters, PlanetScope) are drawn by BasemapStack as layers over a blank style,
// so create with that blank rather than flashing `outdoors` first.
const bm = getBasemap(props.map.basemap)
const initialStyle = bm.kind === 'mapbox' && bm.value ? bm.value : blankStyle()

onMounted(() => {
  const mapInstance = new mapboxgl.Map({
    container: containerRef.value as HTMLElement,
    minZoom: 0,
    maxZoom: 22,
    logoPosition: 'bottom-right',
    style: initialStyle,
    ...(props.initialOptions ?? getLocalMapOptions() ?? defaultOptions),
    transformRequest,
  })
  instance.value = markRaw(mapInstance)
  registerInstance(key, mapInstance)

  mapInstance.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
  mapInstance.addControl(
    new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
    }),
    'bottom-right',
  )

  // style.load fires on the first load AND on every setStyle(); re-init the
  // z-layer anchors each time so beforeId = 'z-N' targets exist before any
  // MapLayer re-adds itself.
  mapInstance.on('style.load', () => {
    zLayers.initialized.value = false
    zLayers.init()
    imageLoader.clear()
    // Re-apply saved view toggles — setStyle wipes added layers/sources, so a
    // shared link (or a basemap swap) must restore labels/3D on each fresh style.
    applyMapViewToggles(mapInstance, props.map.basemap, layersStore.getMapViewSettings(props.map.id))
  })

  mapInstance.on('load', () => {
    ready.value = true
    // Restore this instance's camera from a shared-link hash, if one targets this key.
    const cam = takePendingCamera(key)
    if (cam) mapInstance.jumpTo({ center: [cam[0], cam[1]], zoom: cam[2], bearing: cam[3], pitch: cam[4] })
    mapInstance.on('moveend', () => {
      localStorage.setItem('mapOptions', JSON.stringify({
        center: mapInstance.getCenter(),
        zoom: mapInstance.getZoom(),
        bearing: mapInstance.getBearing(),
        pitch: mapInstance.getPitch(),
      }))
      scheduleWriteUrl()
    })
    mapInstance.on('styleimagemissing', (e: any) => imageLoader.loadImageAndCache(mapInstance, e.id, e.id))
    emit('ready', mapInstance)
  })

  // Keep the canvas matched to its (grid/flex) box, debounced — see
  // RESIZE_SETTLE_MS. Resizing synchronously inside the callback is also what
  // trips the "ResizeObserver loop completed" warning.
  // The observer's first delivery is just the initial box; resize straight
  // through so the map doesn't flash blurred on every mount.
  let firstObservation = true
  resizeObserver = new ResizeObserver(() => {
    if (firstObservation) {
      firstObservation = false
      try { mapInstance.resize() } catch (e) { /* not ready */ }
      return
    }
    resizing.value = true
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      resizeTimer = null
      try { mapInstance.resize() } catch (e) { /* not ready */ }
      resizing.value = false
    }, RESIZE_SETTLE_MS)
  })
  resizeObserver.observe(containerRef.value as HTMLElement)
})

onBeforeUnmount(() => {
  ready.value = false
  if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null }
  if (resizeTimer) { clearTimeout(resizeTimer); resizeTimer = null }
  unregisterInstance(key)
  releaseZLayers(key)
  if (instance.value) {
    try { instance.value.remove() } catch (e) { /* already gone */ }
  }
  instance.value = null
})
</script>

<template>
  <!-- Absolute-fill (not w-full h-full): stretches to the parent's USED height
       even when the flex/grid ancestor chain has no definite height, which is
       what left the canvas at 0 height. The parent must be positioned. -->
  <div class="absolute inset-0 overflow-hidden bg-slate-100">
    <!-- Height via h-full (NOT absolute insets): Mapbox adds `.mapboxgl-map`
         (position: relative) to this element, which would nullify inset-based
         sizing. The parent is a definite height, so h-full resolves. -->
    <div ref="containerRef" class="w-full h-full map-shell" :class="{ 'is-resizing': resizing }"></div>
    <slot v-if="ready && instance" :instance="instance" :ready="ready" />
  </div>
</template>

<style scoped>
/* While a resize is pending the canvas still holds the pre-resize pixel buffer,
   which would otherwise leave a bare strip along the edge that moved. Stretch it
   to fill instead and blur it, so the gesture reads as one frosted settle rather
   than a per-frame flicker. The slight scale pushes the blur's translucent
   fringe outside the parent's overflow clip. */
.map-shell :deep(.mapboxgl-canvas) {
  transition: filter 180ms ease-out, transform 180ms ease-out;
}

.map-shell.is-resizing :deep(.mapboxgl-canvas) {
  width: 100% !important;
  height: 100% !important;
  filter: blur(6px);
  transform: scale(1.04);
}

@media (prefers-reduced-motion: reduce) {
  .map-shell :deep(.mapboxgl-canvas) {
    transition: none;
  }
  .map-shell.is-resizing :deep(.mapboxgl-canvas) {
    filter: none;
    transform: none;
  }
}
</style>
